/**
 * ココシル検索の地図画面
 *
 * @module KokosilMap
 */
import React, { useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { Marker, MarkerClusterer, InfoWindow } from '@react-google-maps/api';
import { Button, Spinner } from 'react-bootstrap';
import { MdGpsFixed } from "react-icons/md";

import * as KokosilConfig from './KokosilConfig';

/**
 * Google Maps での検索結果スポット表示
 *
 * ```js
 * return <KokosilMap searchState={state} />
 * ```
 *
 * searchState には [App#state]{@link module:App~App#state} を渡す。
 */

/** default center */
const defaultCenter = KokosilConfig.MAP_DEFAULT_CENTER;

/** default zoom */
const defaultZoom = KokosilConfig.MAP_DEFAULT_ZOOM;

/** マーカアイコン */
const markerIcon = {
  url: "./images/pin-ucode.png",
  anchor: { x: 10, y: 37 }
};

/**
 * spot の座標を取り出す。
 *
 * @param {Object} spot - [SpotObject]{@link model:App~SpotObject}
 * @returns {Object} `{lat: <latitude>, lng: <longitude>}`
 */
function spotPos(spot) {
  const spotPoint = spot.area.point.match(/(\d*(?:\.\d*)) (\d*(?:\.\d*))/);
  return {
    lng: parseFloat(spotPoint[1]),
    lat: parseFloat(spotPoint[2])
  };
}

/**
 * スポットのマーカー component
 *
 * ```js
 * return (
 *   <SpotMarker
 *     spot={spot}
 *     clusterer={clusterer}
 *     onClickMarker={showSpotInfo}
 *   />
 * );
 * ```
 */
function SpotMarker(props) {
  const spot = props.spot;
  return (
    <Marker
      key={spot.ucode}
      title={spot.name}
      position={spotPos(spot)}
      icon={markerIcon}
      clusterer={props.clusterer}
      onClick={props.onClickMarker.bind(this, spot)}
      options={{spot: spot}}
    />
  );
}

/**
 * スポット情報表示のための InfoWindow の component
 *
 * ```js
 * return <SpotInfoWindow infoWin={infoWIn} onCloseInfoWindow={onCloseInfoWindow} />;
 * ```
 */
function SpotInfoWindow(props) {
  /*
   * <Marker><InfoWindow>...</InfoWindow><Marker> と入れ子にすることで、位置を指
   * 定しなくても Marker の位置に合わせてポップアップさせることが出来るのだが、
   * Clusterer を利用していてズームによって Marker が隠れたときに、InfoWindow を
   * 閉じる「x」だけが DOM に残り、再度 Marker が表示状態になったときに「x」だけ
   * の InfoWindow が表示されるというバグがあるようだ。（Google Maps Javascript
   * API自体にあるのか、react-google-maps/api にあるのかは不明）
   *
   * 入れ子にせず明示的に制御すればこのバグが回避できるので、そのように実装する。
   */
  return (
    <InfoWindow
      position={props.infoWin.position}
      options={props.infoWin.options}
      onCloseClick={props.onCloseInfoWindow}
    >
      <ul className="infoWindow">
      {
        props.infoWin.spots.sort(
          (a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : a.ucode < b.ucode ? -1 : a.ucode > b.ucode ? 1 : 0
        ).map((s, i) => (
          <li class="info-win" key={i}>
          <a href={KokosilConfig.KOKOSIL_TOP + "/ja/place/" + s.ucode}>{s.name}</a>
          </li>
        ))
      }
      </ul>
    </InfoWindow>
  );
}

/**
 * 地図表示 component
 *
 * ```js
 * return (
 *   <KokosilMap searchState={state} />
 * )
 * ```
 *
 * searchState には [App#state]{@link module:App~App#state} を渡す。
 */
function KokosilMap(props) {
  const [infoWin, setInfoWin] = useState(null);

  const showClusterInfo = (cluster) => {
    if (cluster.markers.length > 0) {
      const infoWin = {
        position: { lat: cluster.center.lat(), lng: cluster.center.lng() },
        options: { pixelOffset: { width: 0, height: -12 } },
        spots: cluster.markers.map( m => m.spot ).sort(
          (a, b) => a.ucode < b.ucode ? -1 : a.ucode > b.ucode ? 1 : 0
        )
      };
      setInfoWin(infoWin);
    }
    else {
      setInfoWin(null);
    }
  };

  const showSpotInfo = (spot) => {
    setInfoWin({
      position: spotPos(spot),
      options: { pixelOffset: { width: 0, height: -28 } },
      spots: [spot]
    });
  };

  const onCloseInfoWindow = () => setInfoWin(null);

  const onZoomChanged = () => setInfoWin(null);

  const clusterer = (clusterer) => props.searchState.spots.map(
    (spot, idx) => {
      return (
        <SpotMarker key={spot.ucode} spot={spot} clusterer={clusterer}
          onClickMarker={showSpotInfo} onCloseInfoWindow={onCloseInfoWindow}
        />
      );
    }
  );

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: KokosilConfig.GOOGLE_MAPS_APIKEY
  });

  const [map, setMap] = React.useState(null);
  const [waitGps, setWaitGps] = React.useState(false);

  const onLoad = React.useCallback(map => {
    setMap(map);
  });

  const onUnmount = React.useCallback(map => {
    setMap(null);
  });

  const moveToGps = ev => {
    if (!navigator.geolocation) {
      alert("このブラウザでは現在位置情報にアクセスできません。");
      return;
    }
    setWaitGps(true);
    console.log("waitint getCurrentPosition...");
    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        map.panTo({lat: latitude, lng: longitude});
        setWaitGps(false);
      },
      error => {
        alert("現在位置が取得できませんでした。");
        setWaitGps(false);
      },
      {
        maximumAge: 60 * 1000, // location cache ttl (msec)
        timeout: 30 * 1000,    // (msec)
      }
    );
  };

  const renderMap = () => {
    return (
      <>
      <GoogleMap
        onLoad={onLoad}
        onUnmount={onUnmount}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={defaultCenter}
        zoom={defaultZoom}
        onZoomChanged={onZoomChanged}
        options={{
          disableDefaultUI: true,
          clickableIcons: false,
          zoomControl: true,
          minZoom: KokosilConfig.MAP_MIN_ZOOM
        }} >
        <MarkerClusterer
          gridSize={30}
          averageCenter={true}
          zoomOnClick={false}
          onClick={showClusterInfo}
          options={{imagePath: "./images/m"}} /*
            https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m
            so you must have m1.png, m2.png, m3.png, m4.png, m5.png and m6.png in that folder

            上記 URL から取得したアイコンを ./public/images/ に置いてある。 */
      >
          {clusterer}
        </MarkerClusterer>
        {infoWin &&
        <SpotInfoWindow infoWin={infoWin} onCloseInfoWindow={onCloseInfoWindow} />
        }
      </GoogleMap>
      <Button id="gps-btn" variant="light" onClick={moveToGps} disabled={waitGps}>{ waitGps ? <Spinner size="sm" /> : <MdGpsFixed />}</Button>
      </>
    );
  };

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }
  return isLoaded ? renderMap() : <Spinner />;
}

export default React.memo(KokosilMap)
